/**
 * Prüffunktionen auf Grundlage eines Prüfmakros.
 *
 * `pruefeMitMakro` übernimmt alles, was bei jeder makrogestützten Aufgabe
 * gleich ist: Läuft die Anwendung überhaupt dort, wo VBA verfügbar ist? Lässt
 * sich der Bericht lesen? Hat die lernende Person das Makro schon ausgeführt?
 * Ist beim Auslesen ein Fehler aufgetreten? Erst wenn all das geklärt ist,
 * bekommt die eigentliche Auswertung den Bericht.
 */

import { unsupported } from "@/core/check";
import type { CheckResult, MacroSnippet } from "@/core/types";
import { readPruefbericht, type Pruefbericht } from "./pruefbericht";

const HOST_NAME = {
  Word: "Word",
  Excel: "Excel",
  PowerPoint: "PowerPoint",
} as const;

/** VBA gibt es nur in den Desktop-Fassungen für Windows und Mac. */
export function makrosVerfuegbar(): boolean {
  try {
    const plattform = typeof Office !== "undefined" ? Office.context?.platform : undefined;
    if (!plattform) return true;
    return plattform === Office.PlatformType.PC || plattform === Office.PlatformType.Mac;
  } catch {
    return true;
  }
}

function uhrzeit(datum: Date): string {
  return datum.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Führt eine Prüfung auf Grundlage des Prüfberichts aus.
 *
 * @param makro     Das Prüfmakro der Aufgabe (dasselbe Objekt wie `task.macro`).
 * @param auswerten Bewertet den Bericht. Wird nur aufgerufen, wenn ein
 *                  fehlerfreier Bericht vorliegt.
 */
export async function pruefeMitMakro(
  makro: MacroSnippet,
  auswerten: (bericht: Pruefbericht) => CheckResult | Promise<CheckResult>,
): Promise<CheckResult> {
  const anwendung = HOST_NAME[makro.host];

  if (!makrosVerfuegbar()) {
    return unsupported(
      `Diese Aufgabe wird über ein VBA-Prüfmakro geprüft, weil ${anwendung} die nötige ` +
        `Angabe über Office.js nicht bereitstellt. Makros laufen aber nur in ${anwendung} ` +
        "für Windows und Mac, nicht im Browser oder auf Tablets.",
      "Öffne die Datei in der Desktop-Anwendung, um die Aufgabe prüfen zu lassen, oder " +
        "kontrolliere das Ergebnis selbst. Danach kannst du den Schritt selbst als erledigt markieren.",
    );
  }

  const ergebnis = await readPruefbericht(makro.host, makro.pruefung);

  if (ergebnis.status === "nicht-lesbar") {
    return unsupported(
      `Den Prüfbericht des Makros kann das Add-in erst ab dem Anforderungssatz ` +
        `${ergebnis.satz} ${ergebnis.version} lesen. Diese ${anwendung}-Version stellt ihn nicht bereit.`,
      "Kontrolliere das Ergebnis selbst. Danach kannst du den Schritt selbst als erledigt markieren.",
    );
  }

  if (ergebnis.status === "fehlt") {
    return {
      passed: false,
      message: "Es liegt noch kein Prüfbericht vor – das Prüfmakro wurde noch nicht ausgeführt.",
      hint:
        `Öffne unten „Prüfmakro anzeigen“, kopiere den Code in ein neues VBA-Modul ` +
        `(Alt + F11 → Einfügen → Modul) und starte „${makro.name}“ mit F5. ` +
        "Danach hier erneut auf „Aufgabe prüfen“ klicken.",
    };
  }

  const { bericht } = ergebnis;
  if (bericht.fehler) {
    return {
      passed: false,
      message: `Das Prüfmakro ist auf einen Fehler gestoßen: ${bericht.fehler}`,
      hint:
        "Prüfe, ob die richtige Datei im Vordergrund war, als du das Makro gestartet hast, " +
        "und führe es danach noch einmal aus.",
    };
  }

  const bewertung = await auswerten(bericht);
  const stand = bericht.erstellt ? ` (Prüfbericht vom ${uhrzeit(bericht.erstellt)})` : "";
  const nachAenderung = bewertung.passed
    ? ""
    : " Hast du seitdem etwas geändert, führe das Prüfmakro vor dem nächsten Prüfen erneut aus.";

  return {
    ...bewertung,
    message: `${bewertung.message}${stand}`,
    hint: bewertung.hint ? `${bewertung.hint}${nachAenderung}` : nachAenderung.trim() || undefined,
  };
}

/**
 * Office.js zuerst, Prüfmakro als Ersatzweg.
 *
 * Viele Angaben liefert Office.js nur in neueren Versionen – bei Word etwa
 * über WordApiDesktop, das Word 2019 und 2021 nicht kennen. Dieselbe Angabe
 * liest dann das Prüfmakro. Beide Wege münden in dieselbe Bewertung.
 *
 * @param lesen     Liest die Angabe über Office.js; `null`, wenn der Satz fehlt.
 * @param makro     Prüfmakro für den Ersatzweg.
 * @param ausBericht Baut aus dem Prüfbericht dieselbe Angabe.
 * @param bewerten  Bewertet die Angabe – egal, woher sie stammt.
 */
export async function mitErsatz<T>(
  lesen: () => Promise<T | null>,
  makro: MacroSnippet,
  ausBericht: (bericht: Pruefbericht) => T,
  bewerten: (angabe: T) => CheckResult | Promise<CheckResult>,
): Promise<CheckResult> {
  const angabe = await lesen();
  if (angabe !== null) return bewerten(angabe);
  return pruefeMitMakro(makro, (bericht) => bewerten(ausBericht(bericht)));
}
